/**
 * MatchingActivitySection Component
 * Displays the matching activity log for external payments on a lease
 */

import { useState, useEffect } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { Badge } from '@/components/ui/badge';
import type { badgeVariants } from '@/components/ui/badge-variants';
import {
  getLeaseMatchingActivity,
  type MatchingActivityEntry,
} from '../../lib/api';
import { formatLocalDate } from '../../utils/date';

interface MatchingActivitySectionProps {
  leaseId: string;
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const EVENT_TYPE_VARIANT: Record<MatchingActivityEntry['eventType'], BadgeVariant> = {
  suggested: 'warning',
  confirmed: 'success',
  auto_applied: 'default',
  dismissed: 'danger',
};

const EVENT_TYPE_LABEL: Record<MatchingActivityEntry['eventType'], string> = {
  suggested: 'Suggested',
  confirmed: 'Confirmed',
  auto_applied: 'Auto-Applied',
  dismissed: 'Dismissed',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}


export function MatchingActivitySection({ leaseId }: MatchingActivitySectionProps): JSX.Element {
  const [activity, setActivity] = useState<MatchingActivityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getLeaseMatchingActivity(leaseId, { limit: 50, offset: 0 });
        if (!cancelled) {
          setActivity(data.activity);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load matching activity');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [leaseId]);

  return (
    <section
      data-testid="matching-activity-section"
      aria-label="Matching Activity"
      className="mt-6"
    >
      <h3 className="text-lg font-semibold text-foreground mb-3">Matching Activity</h3>

      {isLoading && (
        <div
          data-testid="matching-activity-loading"
          className="flex flex-col gap-2"
          aria-busy="true"
        >
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 bg-muted rounded-lg animate-pulse"
            />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div
          data-testid="matching-activity-error"
          role="alert"
          className="px-4 py-3 bg-destructive/5 border border-destructive/20 rounded-lg"
        >
          <p className="text-sm text-destructive m-0">{error}</p>
        </div>
      )}

      {!isLoading && !error && activity.length === 0 && (
        <div
          data-testid="matching-activity-empty"
          className="py-10 px-6 bg-card rounded-lg border border-border text-center"
        >
          <p className="text-sm text-muted-foreground m-0">No matching activity yet.</p>
        </div>
      )}

      {!isLoading && !error && activity.length > 0 && (
        <div
          data-testid="matching-activity-list"
          className="flex flex-col gap-2"
          role="list"
        >
          {activity.map((entry) => (
            <div
              key={entry.matchId}
              data-testid={`matching-activity-row-${entry.matchId}`}
              role="listitem"
              className="bg-card rounded-lg border border-border p-4"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate m-0">
                    {entry.transactionDescription}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 m-0">{formatLocalDate(entry.date)}</p>
                  {entry.reasoning && (
                    <p className="text-xs text-muted-foreground mt-1 m-0 italic">{entry.reasoning}</p>
                  )}
                  {entry.confirmedBy && (
                    <p className="text-xs text-muted-foreground mt-0.5 m-0">
                      Confirmed by {entry.confirmedBy}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(entry.amount)}
                  </span>
                  <Badge variant={EVENT_TYPE_VARIANT[entry.eventType]} className="rounded-full">
                    {EVENT_TYPE_LABEL[entry.eventType]}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
